import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import UserModal from "../components/UserModal";
import { apiRequest } from "../api";
import "../css/multi-user-dashboard.css";

const MultiUserUsers = () => {
    const [users, setUsers] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUniversity, setSelectedUniversity] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshList, setRefreshList] = useState(false);
    
    // Filter and sort state
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [sortBy, setSortBy] = useState('first_name');
    const [sortOrder, setSortOrder] = useState('asc');
    
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchData();
    }, [refreshList, selectedUniversity, searchTerm, activeFilter, sortBy, sortOrder]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch universities
            const universitiesData = await apiRequest(`${apiUrl}/multi-user-universities`, "GET", null, token);
            setUniversities(universitiesData);
            
            // Build query parameters for filtering and sorting
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (activeFilter !== '') params.append('is_active', activeFilter);
            if (sortBy) params.append('sort_by', sortBy);
            if (sortOrder) params.append('sort_order', sortOrder);
            
            // Fetch users for selected university or all users if no university selected
            if (selectedUniversity) {
                const usersData = await apiRequest(`${apiUrl}/users/university/${selectedUniversity}?${params.toString()}`, "GET", null, token);
                setUsers(usersData);
            } else {
                // For multi-users, show users from all their universities
                const allUsers = [];
                for (const university of universitiesData) {
                    try {
                        const universityUsers = await apiRequest(`${apiUrl}/users/university/${university.id}?${params.toString()}`, "GET", null, token);
                        allUsers.push(...universityUsers);
                    } catch (error) {
                        console.error(`Error fetching users for university ${university.id}:`, error);
                    }
                }
                setUsers(allUsers);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to load data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveUser = () => {
        console.log("User saved. Refreshing list...");
        setRefreshList((prev) => !prev);
        setSelectedUser(null);
        setShowAddModal(false);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setShowAddModal(true);
    };

    const handleDeleteUser = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}`, "DELETE", null, token);
            // Update the local state instead of refreshing the entire list
            setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        } catch (error) {
            console.error("Error deleting user:", error);
            alert("Failed to delete user. Please try again.");
        }
    };

    const handleActivateUser = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/activate`, "PUT", null, token);
            // Update the local state instead of refreshing the entire list
            setUsers(prevUsers => 
                prevUsers.map(user => 
                    user.id === userId 
                        ? { ...user, is_active: true }
                        : user
                )
            );
        } catch (error) {
            console.error("Error activating user:", error);
            alert("Failed to activate user. Please try again.");
        }
    };

    const handleDisableUser = async (userId) => {
        try {
            await apiRequest(`${apiUrl}/users/${userId}/disable`, "PUT", null, token);
            // Update the local state instead of refreshing the entire list
            setUsers(prevUsers => 
                prevUsers.map(user => 
                    user.id === userId 
                        ? { ...user, is_active: false }
                        : user
                )
            );
        } catch (error) {
            console.error("Error disabling user:", error);
            alert("Failed to disable user. Please try again.");
        }
    };

    const formatDate = (date) => {
        if (!date) return "No Date";
        return new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            year: "numeric",
            month: "short",
            day: "numeric",
        }).format(new Date(date + "Z"));
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="loading">Loading...</div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="error">{error}</div>
                </main>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <main className="main-content">
                <header className="header component">
                    <span className="component-header"> Users</span>
                    <button
                        className="btn btn-primary action"
                        onClick={() => {
                            setSelectedUser(null);
                            setShowAddModal(true);
                        }}
                    >
                        <i className="fa fa-plus"></i> New User
                    </button>
                </header>
                <p><a href="/multi-dashboard">Tour Operations Dashboard</a> &gt; Users</p>
                <hr />

                <div className="filters mb-3">
                    <div className="row">
                        <div className="col-md-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <select
                                value={selectedUniversity}
                                onChange={(e) => setSelectedUniversity(e.target.value)}
                                className="form-control"
                            >
                                <option value="">All Universities</option>
                                {universities.map((university) => (
                                    <option key={university.id} value={university.id}>
                                        {university.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-control"
                                value={activeFilter}
                                onChange={(e) => setActiveFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Disabled</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <select
                                className="form-control"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="first_name">Name</option>
                                <option value="email">Email</option>
                                <option value="university">University</option>
                                <option value="created_on">Created Date</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <select
                                className="form-control"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setActiveFilter('');
                                    setSelectedUniversity('');
                                    setSortBy('first_name');
                                    setSortOrder('asc');
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="users">
                    <table>
                        <thead>
                            <tr>
                                <th>NAME</th>
                                <th>EMAIL</th>
                                <th>UNIVERSITY</th>
                                <th>ROLE</th>
                                <th>STATUS</th>
                                <th>CREATED DATE</th>
                                <th>LAST UPDATED</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length > 0 ? (
                                users.map((user, index) => (
                                    <tr key={user.id} className={index % 2 === 0 ? "even" : "odd"}>
                                        <td className="name">{user.first_name} {user.last_name}</td>
                                        <td>{user.email}</td>
                                        <td>{user.university?.name || "No University"}</td>
                                        <td>{user.role}</td>
                                        <td>
                                            <span className={`status ${user.is_active ? "active" : "inactive"}`}>
                                                {user.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.created_on)}</td>
                                        <td>{formatDate(user.last_updated)}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleEditUser(user)}
                                                >
                                                    Edit
                                                </button>
                                                {user.is_active ? (
                                                    <button 
                                                        className="btn btn-sm btn-warning" 
                                                        onClick={() => handleDisableUser(user.id)}
                                                    >
                                                        Disable
                                                    </button>
                                                ) : (
                                                    <button 
                                                        className="btn btn-sm btn-success" 
                                                        onClick={() => handleActivateUser(user.id)}
                                                    >
                                                        Activate
                                                    </button>
                                                )}
                                                <button 
                                                    className="btn btn-sm btn-danger" 
                                                    onClick={() => handleDeleteUser(user.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center">
                                        {selectedUniversity ? "No users found for this university." : "No users found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {showAddModal && (
                    <UserModal
                        user={selectedUser}
                        onClose={() => setShowAddModal(false)}
                        onSave={handleSaveUser}
                        universities={universities}
                        isMultiUser={true}
                    />
                )}
            </main>
        </div>
    );
};

export default MultiUserUsers; 